import React, { useCallback, useState } from "react";
import AppDataTable from "./AppDataTable";
import Icon from "@mdi/react";
import {
  mdiKeyVariant,
  mdiAutorenew,
  mdiClipboardTextClockOutline,
  mdiAccountCheckOutline,
} from "@mdi/js";
import { enableUserRol } from "../../interpreter/role";
import "./AppDataTable.css";

const getStatusBadgeClass = (label) => {
  if (label === "Activo") return "table-badge table-badge--active";
  if (label === "Vencida" || label === "Eliminada") return "table-badge table-badge--danger";
  return "table-badge table-badge--muted";
};

const isAccountDeleted = (row) => String(row?.account_status) === "0";

export const TechnicianList = ({ openModal, technicians, loading = false, onRefresh }) => {
  const [reactivatingId, setReactivatingId] = useState(null);

  const getSubscriptionStatusLabel = (row) => {
    if (isAccountDeleted(row)) return "Eliminada";
    if (!row?.endDate) return row?.status_sub == 1 ? "Activo" : "Inactivo";
    const endDate = new Date(row.endDate);
    if (Number.isNaN(endDate.getTime()) || endDate < new Date()) return "Vencida";
    return row.status_sub == 1 ? "Activo" : "Inactivo (vigente)";
  };

  const handleReactivate = useCallback(
    async (row) => {
      const userId = row?.id_user;
      if (!userId) {
        alert("No se encontró el usuario asociado a este abogado.");
        return;
      }
      if (!window.confirm(`¿Reactivar la cuenta de ${row.full_name || "este abogado"}?`)) {
        return;
      }
      setReactivatingId(userId);
      try {
        const response = await enableUserRol(String(userId));
        alert(response?.message || "Cuenta reactivada.");
        onRefresh?.();
      } catch (err) {
        console.warn("Error al reactivar cuenta:", err);
        alert("No se pudo reactivar la cuenta. Intenta nuevamente.");
      } finally {
        setReactivatingId(null);
      }
    },
    [onRefresh]
  );

  const columns = [
    { name: "Cod.", selector: row => `00${row.id_technician}`, sortable: true, center: true, width: "90px" },
    { name: "Nombre", selector: row => row.full_name, sortable: true, center: true },
    { name: "C.I.", selector: row => row.ci, sortable: true, center: true, width: "150px" },
    { name: "Última Suscripción", selector: row => row.code_sub ?? "N/A", sortable: true, center: true, width: "190px" },
    { name: "Fecha Acaba", selector: row => row.endDate ?? "N/A", sortable: true, center: true, width: "180px" },
    { name: "Estado", selector: row => getSubscriptionStatusLabel(row), sortable: true, center: true, width: "140px" },
    {
      name: "Acciones",
      cell: (row) => (
        <div className="table-actions table-actions--stack-sm">
          {isAccountDeleted(row) ? (
            <button
              type="button"
              onClick={() => handleReactivate(row)}
              className="table-action table-action--labeled table-action--success"
              title="Reactivar cuenta eliminada"
              disabled={reactivatingId === String(row.id_user)}
            >
              <Icon path={mdiAccountCheckOutline} size={0.95} />
              <span className="table-action__label">
                {reactivatingId === String(row.id_user) ? "Reactivando..." : "Reactivar"}
              </span>
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => openModal("reset", row)}
                className="table-action table-action--labeled table-action--danger"
                title="Restablecer contraseña del abogado"
              >
                <Icon path={mdiKeyVariant} size={0.95} />
                <span className="table-action__label">Restablecer clave</span>
              </button>
              <button
                type="button"
                onClick={() => openModal("subscription", row)}
                className="table-action table-action--labeled table-action--primary"
                title="Renovar suscripción"
              >
                <Icon path={mdiAutorenew} size={0.95} />
                <span className="table-action__label">Renovar plan</span>
              </button>
              <button
                type="button"
                onClick={() => openModal("history", row.id_technician)}
                className="table-action table-action--labeled table-action--secondary"
                title="Historial de suscripciones"
              >
                <Icon path={mdiClipboardTextClockOutline} size={0.95} />
                <span className="table-action__label">Historial</span>
              </button>
            </>
          )}
        </div>
      ),
      center: true,
      width: "200px",
    },
  ];

  return (
    <div className="relative">
      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500 border-solid"></div>
        </div>
      ) : (
        <AppDataTable
            columns={columns}
            noDataComponent={<p className="text-gray-500 py-4">No hay Tecnicos.</p>}
            data={technicians}
            scrollHeight="400px"
            customStyles={{
              headCells: {
                style: {
                  fontWeight: "700",
                  backgroundColor: "#eef2ff", // celeste clarito
                  color: "#374151", // texto gris oscuro
                  fontSize: "15px",
                  paddingTop: "12px",
                  paddingBottom: "12px",
                },
              },
              rows: {
                style: {
                  backgroundColor: "white",
                  "&:hover": {
                    backgroundColor: "#f0f9ff",
                  },
                  transition: "background-color 0.3s",
                },
              },
              cells: {
                style: {
                  fontSize: "14px",
                  color: "#4b5563",
                  paddingTop: "10px",
                  paddingBottom: "10px",
                  justifyContent: "center",
                  textAlign: "center",
                },
              },
            }}
        />
      )}
    </div>
  );
};
